import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Chip from "@mui/material/Chip";
import Grow from "@mui/material/Grow";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import _, { throttle, uniqBy } from "lodash";
import { populateIndividualsByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import { MessageQuery } from "messaging/_api/messaging-api-service";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import {
  getCommentsByField,
  getIsLoadingComments,
  populateThreadComments,
} from "work/proposals/comments/_store/comments-redux-slice";
import CommentItemReadonly from "work/proposals/comments/_view/comment-item-readonly";
import Comment from "work/proposals/comments/comment";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const CommentsContainer = styled("div")(() => ({
  display: "contents",
  height: "100%",
}));
const CommentsList = styled(List)(() => ({
  overflowY: "auto",
}));
const BackToTopChipContainer = styled("div")(() => ({
  display: "flex",
  height: 0,
  justifyContent: "center",
  position: "relative",
  width: "100%",
  zIndex: 1,
}));
const BackToTopChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  top: "20px",
  boxShadow: "0 4px 5px 0 rgb(0 0 0 / 14%), 0 1px 10px 0 rgb(0 0 0 / 12%), 0 2px 4px 0 rgb(0 0 0 / 20%)",
  color: "white",
  position: "absolute",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));

type CommentsReadonlyProps = {
  className?: string;
  proposal: Proposal;
  commentThreads: CommentThread[];
  field: ProposalField;
};

export default function CommentsReadonly(props: Readonly<CommentsReadonlyProps>) {
  const { className, proposal, commentThreads, field } = props;

  const topRef = useRef<HTMLDivElement>(null);
  const commentsListRef = useRef<HTMLUListElement>(null);

  const [backToTopVisible, setBackToTopVisible] = React.useState<boolean>(false);

  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const fieldComments = useSelector(getCommentsByField(proposal.id, field));
  const isLoadingComments = useSelector(getIsLoadingComments(proposal.id, field));

  const handleWindowScrolled = () => {
    if (!commentsListRef?.current) return;
    const scrollPercent = Math.round(
      (commentsListRef?.current.scrollTop /
        (commentsListRef?.current.scrollHeight - commentsListRef?.current.clientHeight)) *
      100
    );
    setBackToTopVisible(scrollPercent > 5);
  };

  const scrollToBottom = () => {
    globalThis.setTimeout(() => {
      topRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    }, 500);
  };

  useEffect(() => {
    const throttledScrollEventHandler = throttle(handleWindowScrolled, Config.backToTopThrottleDelay);

    scrollToBottom();
    // Capture the current value of the ref
    const commentsListElement = commentsListRef.current;
    globalThis.setTimeout(() => {
      commentsListElement?.addEventListener("scroll", throttledScrollEventHandler);
    }, 1000);

    const subscriberIds = uniqBy(
      [
        ...(fieldComments?.map((comment: Comment) => comment.senderId).filter(Boolean) ?? []),
        ...commentThreads.flatMap((thread: CommentThread) => thread.subscribers.map((sub) => sub.userId)),
      ],
      (s) => s?.value
    );

    dispatch(populateIndividualsByUserIds({ session, userIds: subscriberIds as Guid[] }));

    return () => {
      commentsListElement?.removeEventListener("scroll", throttledScrollEventHandler);
    };
  }, [commentThreads, dispatch, fieldComments, session]);

  useEffect(() => {
    if (fieldComments === undefined) {
      commentThreads.forEach((thread) => {
        if (field.isEqualTo(thread.field)) {
          const query = new MessageQuery(
            new MessageParameters(new MessageFilterParameters({})),
            PaginationParameters.create({ pageIndex: 0, pageSize: 100 })
          );
          dispatch(
            populateThreadComments({
              session,
              thread,
              query,
            })
          );
        }
      });
    }
  }, [fieldComments, field, commentThreads, dispatch, session]);

  return isLoadingComments ? (
    <Loader />
  ) : (
    <CommentsContainer className={className}>
      {Boolean(fieldComments?.length && fieldComments.length > 0) && (
        <BackToTopChipContainer>
          <Grow in={backToTopVisible}>
            <BackToTopChip
              label="Jump to latest"
              icon={<ArrowUpwardIcon style={{ color: "white" }} />}
              onClick={() =>
                topRef?.current?.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "nearest",
                })
              }
            />
          </Grow>
        </BackToTopChipContainer>
      )}
      <CommentsList ref={commentsListRef}>
        <div ref={topRef} />
        {(!fieldComments.length) && (
          <ListItem key="NoComments" disableGutters>
            <ListItemText>No comments to display</ListItemText>
          </ListItem>
        )}
        {_(fieldComments)
          .uniqBy(c => c.id?.value)
          .orderBy(c => c.published?.valueOf() ?? 0, 'desc')
          .value()
          .map((comment: Comment) => {
            return <CommentItemReadonly key={comment.id?.value} comment={comment} />;
          })}
      </CommentsList>
    </CommentsContainer>
  );
}
